import { ethers } from 'ethers';
import walletManager from '../wallet/WalletManager.js';
import MetaMaskProvider from '../wallet/MetaMaskProvider.js';
import PrivateKeyProvider from '../wallet/PrivateKeyProvider.js';
import { WALLET_TYPES } from '../wallet/WalletProvider.js';

let provider;
let signer;
let connectionError = '';
let isTestMode = false;
let testWallets = [];
let currentTestWalletIndex = 0;

// Helper function to get RPC URL for a given chain ID
// Uses hardcoded public endpoints to avoid dependency on external files
function getRpcUrl(chainId) {
  const hostname = window.location.hostname;

  switch (chainId) {
    case 31337:
      return 'http://localhost:8545';      // Local Hardhat
    case 31338:
      return 'https://devcli.lazymagpie.de:8546'; // DevCLI
    case 80002:
      return 'https://rpc-amoy.polygon.technology/'; // Polygon Amoy public endpoint
    case 137:
      // Use dedicated GameMaker RPC server for production and preprod
      if (hostname === 'prod.lazymagpie.de' || hostname === 'preprod.lazymagpie.de') {
        return 'https://prod.lazymagpie.de:8547/'; // Dedicated GameMaker RPC server
      }
      return 'https://polygon-rpc.com/'; // Polygon Mainnet public endpoint (fallback)
    default:
      throw new Error(`No RPC URL configured for chain ID ${chainId}`);
  }
}

// Function to detect the appropriate RPC URL for fallback provider
// Only used in development and test environments
function getDefaultRpcUrl() {
  const hostname = window.location.hostname;

  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return getRpcUrl(31337); // Local Hardhat
  } else if (hostname.includes('devcli')) {
    return getRpcUrl(31338); // DevCLI
  } else if (hostname.includes('test') || hostname.includes('amoy')) {
    return getRpcUrl(80002); // Polygon Amoy testnet
  } else if (hostname.includes('preprod')) {
    return getRpcUrl(137); // Polygon Mainnet (preprod) - uses dedicated GameMaker RPC
  } else if (hostname.includes('prod')) {
    return getRpcUrl(137); // Polygon Mainnet (prod) - uses dedicated GameMaker RPC
  } else {
    // For unknown environments, use public Polygon RPC
    return 'https://polygon-rpc.com/';
  }
}

// Network configuration for different environments
const SUPPORTED_NETWORKS = {
  31337: { name: 'Local Hardhat', environment: 'development' },
  31338: { name: 'DevCLI', environment: 'development' },
  80002: { name: 'Polygon Amoy', environment: 'test' },
  137: { name: 'Polygon Mainnet', environment: 'preprod_and_production' }
};

// Function to get expected chainId based on environment
function getExpectedChainId() {
  const hostname = window.location.hostname;

  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 31337; // Local Hardhat
  } else if (hostname.includes('devcli')) {
    return 31338; // DevCLI
  } else if (hostname.includes('test') || hostname.includes('amoy')) {
    return 80002; // Polygon Amoy testnet
  } else if (hostname.includes('preprod')) {
    return 137; // Polygon Mainnet (preprod)
  } else {
    // Default to Polygon mainnet for production
    return 137; // Polygon Mainnet
  }
}

// Helper function to detect if we're in production environment
export function isProductionEnvironment() {
  const hostname = window.location.hostname;
  // Production is when hostname doesn't include dev/test indicators
  return !(hostname.includes('localhost') ||
           hostname.includes('127.0.0.1') ||
           hostname.includes('devcli') ||
           hostname.includes('test') ||
           hostname.includes('amoy') ||
           hostname.includes('preprod'));
}

// Helper function to validate provider network before contract creation
async function validateProviderNetwork(contractProvider, contractAddress) {
  try {
    const network = await contractProvider.getNetwork();
    const expectedChainId = getExpectedChainId();

    if (network.chainId !== expectedChainId) {
      const expectedNetwork = SUPPORTED_NETWORKS[expectedChainId]?.name || `chainId ${expectedChainId}`;
      const currentNetwork = SUPPORTED_NETWORKS[network.chainId]?.name || `chainId ${network.chainId}`;

      console.warn(`⚠️ Network mismatch for contract ${contractAddress}:`);
      console.warn(`   Expected: ${expectedNetwork} (${expectedChainId})`);
      console.warn(`   Current:  ${currentNetwork} (${network.chainId})`);
      console.warn(`   Proceeding anyway - contract calls may fail if deployed on different network`);
    }

    console.log(`✅ Provider network validated: ${SUPPORTED_NETWORKS[network.chainId]?.name || `chainId ${network.chainId}`}`);
    return true;
  } catch (error) {
    console.warn(`⚠️ Provider network validation failed: ${error.message}`);
    // Don't throw - allow contract creation to proceed
    return false;
  }
}

// Initialize wallet providers
const metaMaskProvider = new MetaMaskProvider();
const privateKeyProvider = new PrivateKeyProvider();

// Register providers with wallet manager
walletManager.registerProvider(metaMaskProvider);
walletManager.registerProvider(privateKeyProvider);

// Contract cache to prevent recreation of the same contracts
const contractCache = new Map();

// Request deduplication - prevent multiple simultaneous identical API calls
const ongoingRequests = new Map();

// Selective cache clearing - only clear when contracts actually become invalid
function clearContractCache() {
  console.log('🧹 Clearing entire contract cache due to network change');
  contractCache.clear();
  ongoingRequests.clear();
}

// Clear only signer-dependent contracts when account changes
function clearSignerContracts() {
  console.log('🧹 Clearing signer-dependent contracts due to account change');
  const keysToDelete = [];
  for (const [key, contract] of contractCache.entries()) {
    // Remove contracts that have signer in their cache key
    if (key.includes('_true_') && !key.includes('_no-signer')) {
      keysToDelete.push(key);
    }
  }
  keysToDelete.forEach(key => contractCache.delete(key));
  // Don't clear ongoing requests for read-only operations
}

// Wallet manager event handlers
walletManager.on('providerChanged', ({ newProvider, walletType }) => {
  console.log(`🔄 Active wallet provider changed to: ${walletType}`);
  // Don't clear cache - read-only contracts remain valid
  updateGlobalState();
});

walletManager.on('accountsChanged', (accounts) => {
  console.log('📋 Wallet accounts changed:', accounts);
  clearSignerContracts(); // Only clear signer-dependent contracts
  updateGlobalState();
});

walletManager.on('chainChanged', (chainId) => {
  console.log('🔗 Wallet network changed:', chainId);
  clearContractCache(); // Clear all contracts as they're invalid on different network
  updateGlobalState();
});

// Function to update global state from wallet manager
function updateGlobalState() {
  const oldProvider = provider;
  provider = walletManager.getProvider();
  signer = walletManager.getSigner();
  connectionError = walletManager.getErrorMessage();

}

// Test wallet configuration (Hardhat development accounts)
const TEST_WALLET_CONFIG = [
  {
    privateKey: '0xac0974bec39a17e36ba4a6b4d238ff944bacb478cbed5efcae784d7bf4f2ff80',
    name: 'Test Player 1',
    address: '0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266'
  },
  {
    privateKey: '0x59c6995e998f97a5a0044966f0945389dc9e86dae88c7a8412f4603b6b78690d',
    name: 'Test Player 2', 
    address: '0x70997970C51812dc3A010C7d01b50e0d17dc79C8'
  }
];

// Check if we're in test mode via URL parameter
function checkTestMode() {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('test') === 'true';
  }
  return false;
}

// Initialize test mode Ethers with predefined wallets
function initializeTestMode() {
  console.log('🧪 Test mode detected - initializing test Ethers');
  console.log('🧪 Ethers available:', !!ethers);
  
  try {
    // Use devcli RPC for testing (matches the devcli blockchain network)
    const testRpcUrl = 'https://devcli.lazymagpie.de:8546';
    console.log('🧪 Connecting to RPC:', testRpcUrl);
    
    const oldProvider = provider;
    provider = new ethers.providers.JsonRpcProvider(testRpcUrl);
    console.log('🧪 Ethers provider created:', !!provider);

    if (provider !== oldProvider) {
      console.log('🔄 DEBUG: Provider changed in test mode', {
        oldProvider: !!oldProvider,
        newProvider: !!provider
      });
    }
    
    // CRITICAL: Make provider globally accessible for tests (backward compatibility)
    window.ethers = ethers;
    window.provider = provider;
    
    // Create a mock web3 object for backward compatibility with existing tests
    const mockWeb3 = {
      eth: {
        getAccounts: async () => [testWallets[currentTestWalletIndex]?.address || TEST_WALLET_CONFIG[0].address],
        getBalance: async (address) => {
          const balance = await provider.getBalance(address);
          return balance.toString();
        },
        getBlockNumber: async () => await provider.getBlockNumber(),
        getGasPrice: async () => {
          const gasPrice = await provider.getGasPrice();
          return gasPrice.toString();
        },
        getTransactionReceipt: async (hash) => await provider.getTransactionReceipt(hash),
        getTransactionCount: async (address, tag) => await provider.getTransactionCount(address, tag),
        defaultAccount: TEST_WALLET_CONFIG[0].address,
        net: {
          getId: async () => {
            const network = await provider.getNetwork();
            return network.chainId;
          }
        },
        accounts: {
          privateKeyToAccount: (privateKey) => {
            const wallet = new ethers.Wallet(privateKey);
            return { address: wallet.address, privateKey };
          },
          wallet: {
            add: () => {}, // Mock function
            clear: () => {} // Mock function
          }
        },
        Contract: class {
          constructor(abi, address) {
            this.abi = abi;
            this.address = address;
            this._ethersContract = new ethers.Contract(address, abi, provider);
            
            // Create methods object for Web3 compatibility
            this.methods = {};
            
            // Add contract methods dynamically
            if (abi && Array.isArray(abi)) {
              abi.filter(item => item.type === 'function').forEach(func => {
                this.methods[func.name] = (...args) => ({
                  call: async () => {
                    return await this._ethersContract[func.name](...args);
                  },
                  send: async (options) => {
                    const wallet = testWallets[currentTestWalletIndex];
                    if (!wallet || !wallet.signer) {
                      throw new Error('No test wallet available for transaction');
                    }
                    const contractWithSigner = this._ethersContract.connect(wallet.signer);
                    const tx = await contractWithSigner[func.name](...args, {
                      value: options.value || 0,
                      gasLimit: options.gas || 3000000
                    });
                    await tx.wait(); // Auto-wait for confirmation (Web3.js behavior)
                    return tx;
                  }
                });
              });
            }
          }
        }
      },
      utils: {
        fromWei: (value, unit = 'ether') => ethers.utils.formatUnits(value, unit),
        toWei: (value, unit = 'ether') => ethers.utils.parseUnits(value.toString(), unit),
        toBN: (value) => ethers.BigNumber.from(value),
        solidityKeccak256: (types, values) => ethers.utils.solidityKeccak256(types, values)
      }
    };
    
    window.web3 = mockWeb3;
    console.log('🧪 Mock Web3 exposed globally for backward compatibility:', !!window.web3);
    
    // Initialize test wallets with Ethers
    testWallets = TEST_WALLET_CONFIG.map((walletConfig, index) => {
      const wallet = new ethers.Wallet(walletConfig.privateKey, provider);
      
      return {
        ...walletConfig,
        index: index,
        isTestWallet: true,
        signer: wallet,
        wallet: wallet
      };
    });
    
    // Set current signer to first test wallet initially
    signer = testWallets[0].signer;
    currentTestWalletIndex = 0;
    
    // Update mock web3 default account  
    mockWeb3.eth.defaultAccount = testWallets[0].address;
    
    // Delayed wallet detection (after wallet manager is ready)
    setTimeout(() => {
      try {
        // Try to get current address from wallet manager
        if (walletManager && walletManager.getCurrentAddress) {
          const currentAddress = walletManager.getCurrentAddress();
          if (currentAddress) {
            const matchingWalletIndex = testWallets.findIndex(w => 
              w.address.toLowerCase() === currentAddress.toLowerCase()
            );
            if (matchingWalletIndex >= 0 && matchingWalletIndex !== currentTestWalletIndex) {
              console.log(`🔄 Test mode syncing to active wallet: ${currentAddress} (index: ${matchingWalletIndex})`);
              signer = testWallets[matchingWalletIndex].signer;
              currentTestWalletIndex = matchingWalletIndex;
              mockWeb3.eth.defaultAccount = testWallets[matchingWalletIndex].address;
              
              // Trigger contract reinitialization by forcing React components to refresh
              if (window.testWalletSyncCallback) {
                window.testWalletSyncCallback(matchingWalletIndex);
              }
            }
          }
        }
      } catch (error) {
        console.log('⚠️ Delayed wallet detection failed:', error.message);
      }
    }, 1000); // Give wallet manager time to initialize
    
    // CRITICAL: Make test wallets globally accessible for tests
    window.testWallets = testWallets;
    console.log('🧪 Test wallets exposed globally:', !!window.testWallets);
    
    console.log('✅ Test mode initialized with wallets:');
    testWallets.forEach((wallet, i) => {
      console.log(`  ${i}: ${wallet.name} - ${wallet.address}`);
    });
    
    connectionError = '';
    isTestMode = true;
    
  } catch (error) {
    console.error('❌ Test mode initialization failed:', error);
    console.error('❌ Error details:', error.stack);
    connectionError = `Test mode initialization failed: ${error.message}. Ensure devcli blockchain is running.`;
  }
}

// Function to initialize Ethers - can be called when needed
async function initializeEthers() {
  // Skip wallet manager initialization in test mode
  if (isTestMode) {
    console.log('🧪 Skipping wallet manager initialization in test mode');
    return;
  }

  // Initialize a basic provider for read-only operations (dev/test environments only)
  if (!provider) {
    try {
      const rpcUrl = getDefaultRpcUrl();
      if (rpcUrl) {
        const oldProvider = provider;
        provider = new ethers.providers.JsonRpcProvider(rpcUrl);
        console.log('🔧 Basic provider initialized for read-only operations:', rpcUrl);

        if (provider !== oldProvider) {
          console.log('🔄 DEBUG: Provider changed in fallback initialization', {
            oldProvider: !!oldProvider,
            newProvider: !!provider,
            rpcUrl
          });
        }
      } else {
        console.log('🔧 No fallback provider in production - wallet connection required for blockchain data');
      }
    } catch (error) {
      console.warn('⚠️ Failed to initialize basic provider:', error.message);
    }
  }

  try {
    // Try to auto-connect to previously used wallet
    const autoConnected = await walletManager.autoConnect();
    if (autoConnected) {
      updateGlobalState();
      console.log("✅ Auto-connected to previous wallet");
      return;
    }

    // If auto-connect failed, try MetaMask if available
    if (metaMaskProvider.isAvailable()) {
      try {
        await walletManager.setActiveProvider(WALLET_TYPES.METAMASK);
        await walletManager.connect();
        updateGlobalState();
        console.log("✅ Connected to MetaMask");
      } catch (error) {
        console.log("⚠️ MetaMask connection failed:", error.message);
        connectionError = error.message;
      }
    } else {
      console.log('❌ No wallet provider detected.');
      connectionError = 'No wallet provider detected. Please install MetaMask or use private key connection.';
    }
  } catch (error) {
    console.error("❌ Wallet initialization failed:", error);
    connectionError = `Wallet initialization failed: ${error.message}`;
  }
}

// Function to connect with private key
async function connectWithPrivateKey(privateKey, rpcUrl, persistenceMode = 'session') {
  if (isTestMode) {
    throw new Error('Custom RPC wallet connection not available in test mode');
  }

  try {
    await walletManager.setActiveProvider(WALLET_TYPES.CUSTOM_RPC);
    await privateKeyProvider.connect(privateKey, rpcUrl, persistenceMode);
    updateGlobalState();
    console.log(`✅ Connected with custom RPC wallet (${persistenceMode} mode)`);
    return true;
  } catch (error) {
    console.error("❌ Custom RPC wallet connection failed:", error);
    connectionError = error.message;
    throw error;
  }
}

// Function to get available wallet providers
function getAvailableWalletProviders() {
  if (isTestMode) {
    return []; // Return empty in test mode
  }
  return walletManager.getAvailableProviders();
}

// Function to switch wallet provider
async function switchWalletProvider(walletType) {
  if (isTestMode) {
    throw new Error('Wallet switching not available in test mode');
  }

  try {
    await walletManager.setActiveProvider(walletType);
    updateGlobalState();
    return true;
  } catch (error) {
    connectionError = error.message;
    throw error;
  }
}

// Test wallet management functions
function switchToTestWallet(walletIndex) {
  if (!isTestMode) {
    throw new Error('Not in test mode');
  }

  if (walletIndex < 0 || walletIndex >= testWallets.length) {
    throw new Error(`Invalid wallet index: ${walletIndex}`);
  }

  currentTestWalletIndex = walletIndex;
  signer = testWallets[walletIndex].signer;
  clearSignerContracts(); // Only clear signer-dependent contracts when switching test wallets
  
  // Update mock web3 default account
  if (window.web3) {
    window.web3.eth.defaultAccount = testWallets[walletIndex].address;
  }
  
  console.log(`🔄 Switched to ${testWallets[walletIndex].name}: ${testWallets[walletIndex].address}`);
  
  // Trigger a page reload to refresh all components with new wallet
  window.location.reload();
}

function getCurrentTestWallet() {
  if (!isTestMode) return null;
  return testWallets[currentTestWalletIndex];
}

function getAllTestWallets() {
  return isTestMode ? [...testWallets] : [];
}

// Ethers.js utility functions for easy migration from Web3.js patterns
const ethersUtils = {
  fromWei: (value, unit = 'ether') => ethers.utils.formatUnits(value, unit),
  toWei: (value, unit = 'ether') => ethers.utils.parseUnits(value.toString(), unit),
  toBN: (value) => ethers.BigNumber.from(value),
  formatEther: (value) => ethers.utils.formatEther(value),
  parseEther: (value) => ethers.utils.parseEther(value),
  solidityKeccak256: (types, values) => ethers.utils.solidityKeccak256(types, values)
};

// Helper function to generate cache key for contracts
function getContractCacheKey(address, abiHash, needsSigner, signerAddress, chainId) {
  return `${address.toLowerCase()}_${abiHash}_${needsSigner}_${signerAddress || 'no-signer'}_${chainId || 'unknown-chain'}`;
}

// Helper function to get ABI hash for cache key
function getAbiHash(abi) {
  const abiString = JSON.stringify(abi);
  // Simple hash function for ABI - could be improved but sufficient for caching
  let hash = 0;
  for (let i = 0; i < abiString.length; i++) {
    const char = abiString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString();
}

// Helper function to deduplicate API requests
function deduplicateRequest(requestKey, requestFunction) {
  // If the same request is already ongoing, return the existing promise
  if (ongoingRequests.has(requestKey)) {
    console.log(`🔄 Deduplicating request: ${requestKey}`);
    return ongoingRequests.get(requestKey);
  }

  // Create new request and cache the promise
  const promise = requestFunction().finally(() => {
    // Remove from ongoing requests when completed
    ongoingRequests.delete(requestKey);
  });

  ongoingRequests.set(requestKey, promise);
  return promise;
}

// Helper function to create contract instances
async function createContract(address, abi, needsSigner = false) {
  console.log(`🔧 createContract called - testMode: ${isTestMode}, needsSigner: ${needsSigner}`);

  // Generate cache key
  const abiHash = getAbiHash(abi);
  let signerAddress = null;

  // Get signer address for cache key if available (defensive approach)
  if (needsSigner && !isTestMode && walletManager.getActiveProvider()) {
    try {
      const activeProvider = walletManager.getActiveProvider();
      // Be defensive about getCurrentAddress - it might be async or not exist
      if (activeProvider && typeof activeProvider.getCurrentAddress === 'function') {
        const addressResult = activeProvider.getCurrentAddress();
        // Handle both sync and async returns
        if (typeof addressResult === 'string') {
          signerAddress = addressResult;
        } else if (addressResult && typeof addressResult.then === 'function') {
          // If it's a promise, don't wait for it - use simpler cache key
          signerAddress = 'async-pending';
        }
      }
    } catch (error) {
      // Use a fallback cache key component instead of failing
      signerAddress = 'error-fallback';
      console.log('🔧 Using fallback cache key due to signer address error:', error.message);
    }
  }

  // Get chainId for cache key and validation
  let chainId = 'unknown';
  const contractProvider = needsSigner
    ? (walletManager.getActiveProvider()?.getProvider() || provider)
    : provider;

  if (contractProvider && !isTestMode) {
    try {
      const network = await contractProvider.getNetwork();
      chainId = network.chainId;
    } catch (error) {
      console.warn('⚠️ Could not get network info for cache key:', error.message);
    }
  }

  const cacheKey = getContractCacheKey(address, abiHash, needsSigner, signerAddress, chainId);

  // Check cache first
  if (contractCache.has(cacheKey)) {
    console.log(`🎯 Using cached contract for ${address} (needsSigner: ${needsSigner}, chainId: ${chainId})`);
    return contractCache.get(cacheKey);
  }

  // Validate provider network before creating new contracts (skip for test mode)
  if (!isTestMode && contractProvider) {
    try {
      await validateProviderNetwork(contractProvider, address);
    } catch (error) {
      console.error(`🚫 Contract creation blocked due to network validation failure:`, error.message);
      throw error;
    }
  }

  // In test mode, use the existing logic
  if (isTestMode) {
    console.log(`🔧 Test mode contract creation - provider: ${!!provider}, signer: ${!!signer}`);
    if (!provider) {
      throw new Error('Provider not initialized. Call initializeEthers() first.');
    }
    const contractProvider = needsSigner && signer ? signer : provider;
    console.log(`🔧 Using contractProvider: ${contractProvider === signer ? 'signer' : 'provider'}`);
    const contract = new ethers.Contract(address, abi, contractProvider);
    console.log(`🔧 Contract created successfully: ${!!contract}`);

    // Cache the contract
    contractCache.set(cacheKey, contract);
    return contract;
  }
  
  // Delegate to wallet manager if available
  console.log(`🔧 Non-test mode - walletManager active: ${!!walletManager.getActiveProvider()}`);
  if (walletManager.getActiveProvider()) {
    try {
      const contract = walletManager.createContract(address, abi, needsSigner);
      console.log(`🔧 WalletManager contract created: ${!!contract}`);

      // Cache the contract
      contractCache.set(cacheKey, contract);
      return contract;
    } catch (error) {
      console.log(`🔧 WalletManager contract creation failed: ${error.message}`);
      throw error;
    }
  }

  // For read-only operations, use provider even when no wallet connected
  if (!needsSigner && provider) {
    console.log('🔧 No wallet connected - creating read-only contract with provider');
    console.log(`🔧 Contract details: address=${address}, abiLength=${abi?.length}, provider=${!!provider}`);
    const contract = new ethers.Contract(address, abi, provider);
    console.log(`🔧 Contract created, available methods: ${Object.getOwnPropertyNames(contract).filter(name => typeof contract[name] === 'function').slice(0, 5).join(', ')}`);

    // Cache the contract
    contractCache.set(cacheKey, contract);
    return contract;
  }

  // If no provider available, return null
  if (!needsSigner) {
    console.log('🔧 No provider available - returning null for read-only contract');
    return null;
  }

  // For transactions that need signer, wallet connection is required
  throw new Error('No wallet connected. Please connect your wallet first.');
}

// Helper function to validate current network
async function validateNetwork() {
  if (isTestMode) {
    return { isValid: true }; // Skip validation in test mode
  }

  // Delegate to active wallet provider if available
  const activeProvider = walletManager.getActiveProvider();
  if (activeProvider && typeof activeProvider.validateNetwork === 'function') {
    return await activeProvider.validateNetwork();
  }

  // Fallback to original logic for compatibility
  if (!provider) {
    return {
      isValid: false,
      error: 'No wallet provider found. Please install a wallet or connect with private key.',
      needsConnection: true
    };
  }

  try {
    const network = await provider.getNetwork();
    const currentChainId = network.chainId;
    const networkInfo = SUPPORTED_NETWORKS[currentChainId];
    
    if (!networkInfo) {
      const supportedChains = Object.entries(SUPPORTED_NETWORKS)
        .map(([id, info]) => `${info.name} (${id})`)
        .join(', ');
      
      return {
        isValid: false,
        error: `Unsupported network detected (Chain ID: ${currentChainId}). Please switch to one of: ${supportedChains}`,
        currentChainId,
        supportedNetworks: SUPPORTED_NETWORKS
      };
    }
    
    return { 
      isValid: true, 
      network: networkInfo,
      chainId: currentChainId
    };
  } catch (error) {
    return {
      isValid: false,
      error: `Network validation failed: ${error.message}`
    };
  }
}

// Helper function to validate signer connection  
async function validateSigner() {
  if (isTestMode) {
    return { isValid: true }; // Skip validation in test mode
  }

  // Delegate to active wallet provider if available
  const activeProvider = walletManager.getActiveProvider();
  if (activeProvider && typeof activeProvider.validateSigner === 'function') {
    return await activeProvider.validateSigner();
  }

  // Fallback to original logic for compatibility
  if (!provider) {
    return {
      isValid: false,
      error: 'No Web3 provider available.',
      needsConnection: true
    };
  }

  try {
    const accounts = await provider.listAccounts();
    if (accounts.length === 0) {
      return {
        isValid: false,
        error: 'No accounts connected. Please connect your wallet.',
        needsConnection: true
      };
    }

    if (!signer) {
      return {
        isValid: false,
        error: 'Wallet connected but signer not available. Please unlock your wallet.',
        needsConnection: true
      };
    }

    // Verify signer can get address
    await signer.getAddress();
    
    return { 
      isValid: true,
      account: accounts[0]
    };
  } catch (error) {
    return {
      isValid: false,
      error: `Signer validation failed: ${error.message}`,
      needsConnection: true
    };
  }
}

// Helper function to get accounts (equivalent to web3.eth.getAccounts())
async function getAccounts() {
  if (isTestMode) {
    return [testWallets[currentTestWalletIndex]?.address || TEST_WALLET_CONFIG[0].address];
  }
  
  // Delegate to wallet manager
  return await walletManager.getAccounts();
}

// Initialize based on mode
if (checkTestMode()) {
  initializeTestMode();
} else {
  // For normal mode, initialize provider only (no automatic wallet connection)
  if (window.ethereum) {
    const oldProvider = provider;
    provider = new ethers.providers.Web3Provider(window.ethereum);
    // Don't automatically initialize wallet connection to prevent eth_requestAccounts
    // on pages that don't need wallet access (like information page)
    console.log('🔗 Ethers provider ready - wallet connection deferred until explicitly requested');

  }
}

export {
  provider,
  signer,
  connectionError,
  isTestMode,
  testWallets,
  switchToTestWallet,
  getCurrentTestWallet,
  getAllTestWallets,
  ethersUtils,
  createContract,
  clearContractCache,
  deduplicateRequest,
  getAccounts,
  initializeEthers,
  validateNetwork,
  validateSigner,
  // New wallet management functions
  connectWithPrivateKey,
  getAvailableWalletProviders,
  switchWalletProvider,
  walletManager,
  WALLET_TYPES
};